import SkillChip from '@/skills/components/SkillChip'
import { SkillsActions } from '@/skills/store/slice'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import * as Sentry from '@sentry/browser'
import NewChatSkillsError from './new-chat-skills-error'
import AllSkillsDialog from '../../../routes/dashboard/skills/components/AllSkillsDialog'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent, SkillSource } from '@/analytics/schema/events.schema'

interface NewChatSkillsProps {
  conversationId: string
}

export default function NewChatSkills(props: NewChatSkillsProps) {
  const { conversationId } = props
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Get a list of the skills from the store
  const skillsData = useAppSelector((state: RootState) => state.skillsState.skillsContentData)
  const skills = skillsData.skills

  // Check if the skills data is empty
  if (skills.length === 0) {
    // Should not happen, but if it does, log an error
    Sentry.captureException('NewChatSkills component loaded with no skills data')
    return null
  }

  // The UX splits the skills into two rows that horizontally scroll / overrun
  const mid = Math.ceil(skills.length / 2)
  const firstRow = skills.slice(0, mid)
  const secondRow = skills.slice(mid)

  return (
    <div className={'px-2'}>
      <div className={'flex flex-nowrap items-center justify-between mb-4'}>
        <div className={'font-medium text-sm leading-5'}>Select a skill or enter a prompt to get started</div>
        <div className={'hidden xs:flex flex-nowrap items-center gap-x-1'}>
          <AllSkillsDialog conversationId={conversationId} />
        </div>
      </div>

      {/* Two fixed rows, scrolls only horizontally */}
      <div className="grow general-table-wrapper-overflow-fix mb-3 pb-1 md:mb-10 md:pb-4">
        <div className="flex gap-x-2 xs:mb-2">
          {firstRow.map((skill) => (
            <SkillChip
              key={skill.skill}
              content={skill}
              onClick={() => {
                // Track quick action click
                trackEvent(AnalyticsEvent.SkillQuickActionClick, {
                  skillSource: SkillSource.quick_action,
                  skill: skill.skill,
                })

                // Track prompt submission (quick action)
                trackEvent(AnalyticsEvent.SkillPromptSubmitted, {
                  skill: skill.skill,
                  skillSource: SkillSource.quick_action,
                })

                dispatch(
                  SkillsActions.initializeConversationWithSkill({
                    conversationId,
                    skill: skill.skill,
                    skillSource: SkillSource.quick_action,
                  })
                )
              }}
            />
          ))}
        </div>
        <div className="hidden xs:flex gap-x-2">
          {secondRow.map((skill) => (
            <SkillChip
              key={skill.skill}
              content={skill}
              onClick={() => {
                trackEvent(AnalyticsEvent.SkillQuickActionClick, {
                  skillSource: SkillSource.quick_action,
                  skill: skill.skill,
                })
                trackEvent(AnalyticsEvent.SkillPromptSubmitted, {
                  skill: skill.skill,
                  skillSource: SkillSource.quick_action,
                })

                dispatch(
                  SkillsActions.initializeConversationWithSkill({
                    conversationId,
                    skill: skill.skill,
                    skillSource: SkillSource.quick_action,
                  })
                )
              }}
            />
          ))}
        </div>
      </div>

      <div className={'flex xs:hidden flex-nowrap items-center justify-end gap-x-1 mb-4'}>
        <AllSkillsDialog />
      </div>

      <NewChatSkillsError />
    </div>
  )
}
